import PropTypes from "prop-types";
import React from "react";
import { Button, ButtonToolbar, Col, Form, Row } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";
import { FormattedMessage, useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import AsyncSelect from "react-select/async";

import genericTranslations from "../../../utils/genericTranslations.js";
import { emptyDirection } from "../directions.js";
import { searchStations } from "../thunks.js";

const messages = {
  stationName: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.stationName",
    description: "Station name label",
    defaultMessage: "Station name",
  },
  signalImageRequired: {
    id: "modules.TrainStopSignalImages.components.SignalImagesList.signalImageRequired",
    description: "Signal image required label",
    defaultMessage: "Signal image required",
  },
  directions: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.directions",
    description: "Directions label",
    defaultMessage: "Only when the train runs towards",
  },
  directionsPlaceholder: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.directionsPlaceholder",
    description: "Directions search placeholder",
    defaultMessage: "Search station...",
  },
  directionsHelp: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.directionsHelp",
    description: "Directions help text",
    defaultMessage:
      "The signal image is only required when the train stops at one of these stations later on the trip. Optionally limit a direction to one train series. Without directions, the signal image is required in all directions.",
  },
  trainSeries: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.trainSeries",
    description: "Train series input label",
    defaultMessage: "Train series",
  },
  allTrainSeries: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.allTrainSeries",
    description: "Train series placeholder, empty means all series",
    defaultMessage: "All series",
  },
  addDirection: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.addDirection",
    description: "Add direction button",
    defaultMessage: "Add direction",
  },
  removeDirection: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.removeDirection",
    description: "Remove direction button",
    defaultMessage: "Remove direction",
  },
  trainSeriesHelp: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.trainSeriesHelp",
    description: "Explains how the train series follows from the trip number",
    defaultMessage:
      "Train series = trip number rounded down to hundreds, e.g. 32452 → 32400",
  },
  noStationsFound: {
    id: "modules.TrainStopSignalImages.components.SignalImagesForm.noStationsFound",
    description: "No stations found in directions search",
    defaultMessage: "No stations found",
  },
};

const toOption = ({ stopCode, stationName }) =>
  stopCode ? { value: stopCode, label: stationName } : null;

const SignalImagesForm = ({
  handleChange,
  handleSubmit,
  isSubmitting,
  setFieldValue,
  onClickDelete,
  errors,
  values,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const directions = values.directions || [];
  const directionErrors = Array.isArray(errors.directions)
    ? errors.directions
    : [];

  const updateDirection = (index, changes) =>
    setFieldValue(
      "directions",
      directions.map((direction, i) =>
        i === index ? { ...direction, ...changes } : direction,
      ),
    );

  const removeDirection = (index) =>
    setFieldValue(
      "directions",
      directions.filter((_, i) => i !== index),
    );

  const loadStationOptions = async (query) => {
    let stations = [];
    try {
      stations = await dispatch(searchStations(query));
    } catch (err) {
      console.error("Error searching stations:", err);
    }

    return stations
      .filter((station) => station.id?.code !== values.stopCode)
      .map((station) =>
        toOption({
          stopCode: station.id.code,
          stationName: station.names.long,
        }),
      );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group controlId="stationName" className="mb-4">
        <Form.Label>{intl.formatMessage(messages.stationName)}</Form.Label>
        <Form.Control
          type="text"
          autoComplete="off"
          value={values.stationName || ""}
          onChange={handleChange}
          isInvalid={!values.stationName}
          disabled={!window.location.pathname.includes("find")}
        />
        <Form.Control.Feedback type="invalid">
          {errors.stationName}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group controlId="signalImageRequired" className="mb-4">
        <Form.Check
          type="switch"
          label={intl.formatMessage(messages.signalImageRequired)}
          checked={values.signalImageRequired ?? true}
          onChange={(e) =>
            setFieldValue("signalImageRequired", e.target.checked)
          }
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>{intl.formatMessage(messages.directions)}</Form.Label>

        {directions.map((direction, index) => (
          <Row key={index} className="mb-2 align-items-start">
            <Col md={6}>
              <AsyncSelect
                inputId={`directions-${index}-station`}
                cacheOptions
                placeholder={intl.formatMessage(messages.directionsPlaceholder)}
                noOptionsMessage={() =>
                  intl.formatMessage(messages.noStationsFound)
                }
                loadOptions={loadStationOptions}
                value={toOption(direction)}
                onChange={(selected) =>
                  updateDirection(index, {
                    stopCode: selected?.value || "",
                    stationName: selected?.label || "",
                  })
                }
                isDisabled={!values.signalImageRequired}
              />
              {directionErrors[index]?.stopCode && (
                <Form.Text className="text-danger">
                  {directionErrors[index].stopCode}
                </Form.Text>
              )}
            </Col>
            <Col md={4}>
              <Form.Control
                type="number"
                min={100}
                step={100}
                aria-label={intl.formatMessage(messages.trainSeries)}
                placeholder={intl.formatMessage(messages.allTrainSeries)}
                value={direction.trainSeries}
                onChange={(e) =>
                  updateDirection(index, { trainSeries: e.target.value })
                }
                isInvalid={!!directionErrors[index]?.trainSeries}
                disabled={!values.signalImageRequired}
              />
              <Form.Control.Feedback type="invalid">
                {directionErrors[index]?.trainSeries}
              </Form.Control.Feedback>
              {!directionErrors[index]?.trainSeries && (
                <Form.Text>
                  {intl.formatMessage(messages.trainSeriesHelp)}
                </Form.Text>
              )}
            </Col>
            <Col md={2}>
              <Button
                variant="outline-danger"
                title={intl.formatMessage(messages.removeDirection)}
                aria-label={intl.formatMessage(messages.removeDirection)}
                onClick={() => removeDirection(index)}
                disabled={!values.signalImageRequired}
              >
                <FaTrash />
              </Button>
            </Col>
          </Row>
        ))}

        <div>
          <Button
            variant="light"
            size="sm"
            onClick={() =>
              setFieldValue("directions", [...directions, emptyDirection()])
            }
            disabled={!values.signalImageRequired}
          >
            <FaPlus className="me-1" />
            {intl.formatMessage(messages.addDirection)}
          </Button>
        </div>
        <Form.Text className="d-block mt-2">
          {intl.formatMessage(messages.directionsHelp)}
        </Form.Text>
      </Form.Group>

      <hr />

      <Form.Group controlId="formActions" className="mb-3">
        <ButtonToolbar>
          {location.pathname.includes("edit") && (
            <Button variant="danger" className="me-2" onClick={onClickDelete}>
              <FormattedMessage {...genericTranslations.delete} />
            </Button>
          )}

          <Button
            type="submit"
            variant="light"
            disabled={isSubmitting || Object.keys(errors).length > 0}
          >
            <FormattedMessage {...genericTranslations.save} />
          </Button>
        </ButtonToolbar>
      </Form.Group>
    </Form>
  );
};

SignalImagesForm.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    stationName: PropTypes.string,
    signalImageRequired: PropTypes.string,
    directions: PropTypes.arrayOf(
      PropTypes.shape({
        stopCode: PropTypes.string,
        trainSeries: PropTypes.string,
      }),
    ),
  }),
  values: PropTypes.shape({
    stopCode: PropTypes.string,
    stationName: PropTypes.string,
    signalImageRequired: PropTypes.bool,
    directions: PropTypes.arrayOf(
      PropTypes.shape({
        stopCode: PropTypes.string,
        stationName: PropTypes.string,
        trainSeries: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default SignalImagesForm;
